import * as React from "react";
import { cva } from "class-variance-authority";
import { ChevronDown, Inbox } from "lucide-react";
import { cn } from "../../lib/utils";

const surfaceClass =
  "rounded-customer border border-slate-200 bg-white shadow-customer";

const actionButtonVariants = cva(
  "inline-flex min-h-touch items-center justify-center gap-ui-2 rounded-customer px-ui-4 py-ui-2 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-slate-900 text-white hover:bg-brand",
        accent:
          "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-lg shadow-blue-600/20 hover:brightness-110",
        secondary:
          "border border-slate-300 bg-white text-slate-700 hover:border-brand hover:bg-blue-50 hover:text-brand",
        quiet:
          "bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-950",
        destructive:
          "border border-red-200 bg-white text-red-700 hover:bg-red-50",
      },
      size: {
        default: "",
        compact: "px-ui-3 text-xs",
        icon: "min-w-touch px-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

const statusTone = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  danger: "border-red-200 bg-red-50 text-red-700",
  info: "border-blue-200 bg-blue-50 text-blue-700",
  neutral: "border-slate-200 bg-slate-100 text-slate-600",
};

export function ActionButton({
  className,
  variant,
  size,
  type = "button",
  ...props
}) {
  return (
    <button
      type={type}
      className={cn(actionButtonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

const customerFieldClass =
  "min-h-touch w-full rounded-customer border border-slate-300 bg-white px-ui-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2";

export function CustomerInput({ className, ...props }) {
  return <input className={cn(customerFieldClass, className)} {...props} />;
}

export function CustomerSelect({ className, ...props }) {
  return <select className={cn(customerFieldClass, className)} {...props} />;
}

export function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
  active = false,
  onClick,
  className,
  valueClassName,
  labelClassName,
  ...props
}) {
  const classes = cn(
    surfaceClass,
    "group w-full p-ui-4 text-left transition",
    onClick && "min-h-[8.75rem] hover:-translate-y-0.5 hover:border-brand/50",
    active && "border-brand ring-2 ring-brand/15",
    className,
  );
  const content = (
    <>
      <div className="flex items-center justify-between gap-ui-3">
        {Icon ? (
          <span className="flex h-9 w-9 items-center justify-center rounded-customer bg-blue-50 text-brand">
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
        ) : (
          <span aria-hidden="true" />
        )}
        {onClick ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-400">
            View
          </span>
        ) : null}
      </div>
      <div data-testid="metric-value" className={cn("mt-ui-4 font-mono text-2xl font-bold text-slate-950", valueClassName)}>{value}</div>
      <div className={cn("mt-ui-1 text-sm font-medium text-slate-600", labelClassName)}>{label}</div>
      {detail ? <p data-testid="metric-detail" className="mt-ui-2 text-xs leading-5 text-slate-500">{detail}</p> : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        aria-pressed={active}
        onClick={onClick}
        className={classes}
        {...props}
      >
        {content}
      </button>
    );
  }

  return (
    <article className={classes} {...props}>
      {content}
    </article>
  );
}

export function StatusBadge({ tone = "neutral", className, children, ...props }) {
  return (
    <span
      className={cn(
        "inline-flex min-h-7 items-center gap-ui-1 rounded-customer border px-ui-2 font-mono text-[10px] font-semibold uppercase tracking-[0.12em]",
        statusTone[tone] || statusTone.neutral,
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

export function CustomerPanel({ title, description, className, children, ...props }) {
  return (
    <section className={cn(surfaceClass, "overflow-hidden", className)} {...props}>
      {(title || description) && (
        <header className="border-b border-slate-200 px-ui-4 py-ui-4 sm:px-ui-5">
          {title ? <h2 className="text-lg font-bold text-slate-950">{title}</h2> : null}
          {description ? <p className="mt-ui-1 text-sm text-slate-500">{description}</p> : null}
        </header>
      )}
      {children}
    </section>
  );
}

export function TableWrapper({ title, description, className, children, ...props }) {
  return (
    <CustomerPanel
      title={title}
      description={description}
      className={className}
      data-customer-table-wrapper="true"
      {...props}
    >
      <div className="w-full overflow-x-auto">{children}</div>
    </CustomerPanel>
  );
}

export function DisclosurePanel({
  summary,
  description,
  className,
  children,
  ...props
}) {
  return (
    <details
      className={cn(
        "group rounded-customer border border-slate-200 bg-slate-50/70",
        className,
      )}
      {...props}
    >
      <summary className="flex min-h-touch cursor-pointer list-none items-center justify-between gap-ui-3 rounded-customer px-ui-4 py-ui-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
        <span>
          <span className="block">{summary}</span>
          {description ? (
            <span className="mt-ui-1 block text-xs font-normal text-slate-500">
              {description}
            </span>
          ) : null}
        </span>
        <ChevronDown
          className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className="border-t border-slate-200 px-ui-4 pb-ui-4">{children}</div>
    </details>
  );
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
  ...props
}) {
  return (
    <div
      className={cn("flex flex-col items-center px-ui-5 py-ui-6 text-center", className)}
      {...props}
    >
      <span className="flex h-11 w-11 items-center justify-center rounded-customer bg-slate-100 text-slate-400">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <h3 className="mt-ui-3 text-sm font-semibold text-slate-900">{title}</h3>
      {description ? (
        <p className="mt-ui-1 max-w-md text-sm leading-6 text-slate-500">{description}</p>
      ) : null}
      {action ? <div className="mt-ui-4">{action}</div> : null}
    </div>
  );
}

function outcomeProgressLabel(progress) {
  const corrected = Number(progress?.issues_corrected || 0);
  const awaiting = Number(progress?.actions_awaiting_approval || 0);
  const percentage = progress?.percentage_improvement;
  if (corrected === 0 && awaiting === 0) return "Not started";
  if (corrected === 0 && awaiting > 0) return "Ready to approve";
  if (corrected > 0 && Number(percentage) === 0) return "<1%";
  return percentage == null ? "Not measured" : `${percentage}%`;
}

function outcomeDate(value) {
  if (!value) return "Date unavailable";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function occurrenceValue(count, contacts) {
  if (count === 0) return "No issues detected";
  const occurrences = `${Number(count).toLocaleString()} issue occurrence${count === 1 ? "" : "s"}`;
  return contacts == null
    ? occurrences
    : `${occurrences} across ${Number(contacts).toLocaleString()} contacts`;
}

export function ScanOutcomeSummary({ summary, loading = false }) {
  if (loading) {
    return (
      <CustomerPanel className="mb-ui-6">
        <EmptyState
          title="Loading outcome summary…"
          description="CleanIQ is comparing this CRM's durable baseline with its latest completed scan."
        />
      </CustomerPanel>
    );
  }
  if (!summary) {
    return (
      <CustomerPanel className="mb-ui-6">
        <EmptyState
          title="Outcome summary unavailable"
          description="Complete a scan to establish a durable CRM baseline."
        />
      </CustomerPanel>
    );
  }

  const { baseline, current, correction_progress: progress } = summary;
  const comparisonAvailable = Boolean(summary.comparison_available);
  const appliedCorrections = Number(progress?.issues_corrected || 0);
  const hasAppliedCorrections = appliedCorrections > 0;
  const hasMeasuredOutcome = hasAppliedCorrections && comparisonAvailable;
  const notStarted =
    appliedCorrections === 0
    && Number(progress?.actions_awaiting_approval || 0) === 0;
  const healthChanged =
    comparisonAvailable
    && baseline.health_score != null
    && current.health_score != null
    && Number(baseline.health_score) !== Number(current.health_score);

  const panelTitle = hasMeasuredOutcome
    ? "Baseline to current outcome"
    : comparisonAvailable
      ? "Scan-to-scan comparison"
      : "CRM baseline";
  const panelDescription = hasMeasuredOutcome
    ? `Baseline is this CRM's first completed scan (${outcomeDate(baseline.completed_at)}) and never changes. Current is the newest completed scan (${outcomeDate(current.completed_at)}).`
    : comparisonAvailable
      ? `This compares the first completed scan (${outcomeDate(baseline.completed_at)}) with the newest completed scan (${outcomeDate(current.completed_at)}). No CleanIQ corrections have been applied yet.`
      : hasAppliedCorrections
        ? `This first completed scan (${outcomeDate(baseline.completed_at)}) establishes the CRM baseline. Corrections have been applied, but another completed scan is required to measure their result.`
        : `This first completed scan (${outcomeDate(baseline.completed_at)}) establishes the CRM baseline. No comparison or CleanIQ correction result exists yet.`;
  const healthDetail = hasMeasuredOutcome
    ? "First scan to latest scan"
    : !comparisonAvailable
      ? "One completed scan; run another after CRM changes to compare."
      : healthChanged
        ? "No CleanIQ corrections were applied; the underlying CRM data changed between scans."
        : "No CleanIQ corrections were applied; measured health is unchanged.";

  return (
    <CustomerPanel
      className="mb-ui-6"
      data-testid="scan-outcome-summary"
      title={panelTitle}
      description={panelDescription}
    >
      <div className="grid gap-ui-3 p-ui-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="CRM health"
          value={
            baseline.health_score == null || current.health_score == null
              ? "Not measured"
              : comparisonAvailable
                ? `${baseline.health_score} → ${current.health_score}`
                : `${current.health_score}`
          }
          detail={healthDetail}
        />
        <MetricCard
          label="Latest measured issues"
          value={occurrenceValue(current.issue_occurrences, current.total_contacts)}
          detail="One contact can contribute multiple issue occurrences."
        />
        <MetricCard
          label="Correction progress"
          value={outcomeProgressLabel(progress)}
          detail={
            notStarted
              ? "No field corrections have been applied or queued for approval."
              : `${appliedCorrections.toLocaleString()} field correction${appliedCorrections === 1 ? "" : "s"} applied.`
          }
        />
        <MetricCard
          label="Awaiting approval"
          value={progress.actions_awaiting_approval > 0 ? progress.actions_awaiting_approval : "None"}
          detail={
            progress.actions_awaiting_approval > 0
              ? "Approve these corrections, then run a fresh scan to measure the result."
              : "No field corrections are currently queued for approval."
          }
        />
      </div>
      <div className="border-t border-slate-200 px-ui-4 py-ui-3 text-xs leading-5 text-slate-500">
        The baseline recorded {occurrenceValue(baseline.issue_occurrences, baseline.total_contacts)}.
        Applied corrections are counted from stored write-back records; the latest scan remains the measured CRM state.
      </div>
    </CustomerPanel>
  );
}

export { actionButtonVariants, customerFieldClass, surfaceClass };
